import { TableColumnDelta } from '../../api/types/interfaces';
import { sameValue } from '../../services/preferences/preference-rows.util';
import { CODE_DEFAULTS } from './table-columns.defaults';
import { asDelta } from './table-columns.merge';

/**
 * NBO-2516: column layouts on the account. One `dataTable` preference per table, GLOBAL scope, keyed
 * by the table's path in the tableColumns slice joined with dots (e.g.
 * `ordersListing.boOrdersTableColumns`). The value is the table's delta (NBO-2165) — attribute
 * columns stay namespaced per tenant inside it, widths ride along (NBO-2408).
 *
 * The key is permanent: it is stored on the account. Never rename a slice path without migrating.
 */
export const DATA_TABLE_PREFERENCE = 'dataTable';

const PATH_SEPARATOR = '.';

export const tablePreferenceKey = (segments: readonly (string | number)[]): string => segments.join(PATH_SEPARATOR);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isDeltaLike = (value: Record<string, unknown>): boolean =>
  ['displayed', 'available', 'hidden', 'displayedColumns', 'availableColumns'].some((field) =>
    Array.isArray(value[field])
  );

/** True when `segments` names a table the code knows (a CODE_DEFAULTS leaf). */
export function isKnownTablePath(segments: readonly string[]): boolean {
  let node: unknown = CODE_DEFAULTS;
  for (const segment of segments) {
    if (!isPlainObject(node)) return false;
    node = node[segment];
  }
  return isPlainObject(node) && Array.isArray(node['displayedColumns']);
}

/**
 * A delta that changes nothing: no order, no widths, no attribute columns. Merges to the code
 * defaults exactly, so it is stored as no row at all (an empty value is a delete, never a row).
 */
export function isEmptyDelta(delta: TableColumnDelta | undefined): boolean {
  if (!delta) return true;
  return (
    !delta.displayed?.length &&
    !delta.available?.length &&
    !delta.hidden?.length &&
    !Object.keys(delta.widths ?? {}).length &&
    !Object.values(delta.attributeColumns ?? {}).some((descriptors) => descriptors?.length)
  );
}

/**
 * The slice as path → delta, one entry per table holding a non-empty delta.
 * Paths the code does not know are left out — that is also what drops the stray
 * `templateErrorLogs*.templatesTableColumns` leaves the pre-NBO-2516 reducer wrote.
 */
export function flattenTableColumns(tree: unknown): Record<string, TableColumnDelta> {
  const out: Record<string, TableColumnDelta> = {};
  const walk = (node: unknown, path: string[]) => {
    if (!isPlainObject(node)) return;
    if (isDeltaLike(node)) {
      const delta = asDelta(node);
      if (delta && !isEmptyDelta(delta) && isKnownTablePath(path)) out[path.join(PATH_SEPARATOR)] = delta;
      return;
    }
    for (const [key, child] of Object.entries(node)) walk(child, [...path, key]);
  };
  walk(tree, []);
  return out;
}

/** The stored rows back as a slice-shaped tree. Unknown paths and unreadable values are skipped. */
export function tableColumnsFromPreferences(values: Record<string, unknown>): Record<string, unknown> {
  const tree: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    const segments = key.split(PATH_SEPARATOR);
    if (!isKnownTablePath(segments)) continue;
    const delta = asDelta(value);
    if (!delta || isEmptyDelta(delta)) continue;
    let node = tree;
    for (const segment of segments.slice(0, -1)) {
      if (!isPlainObject(node[segment])) node[segment] = {};
      node = node[segment] as Record<string, unknown>;
    }
    node[segments[segments.length - 1]] = delta;
  }
  return tree;
}

/** What changed between two flattened layouts: tables to store and tables to remove (reset to defaults). */
export function diffTableColumns(
  before: Record<string, TableColumnDelta>,
  after: Record<string, TableColumnDelta>
): { changed: Record<string, TableColumnDelta>; removed: string[] } {
  const changed: Record<string, TableColumnDelta> = {};
  for (const [path, delta] of Object.entries(after)) {
    if (!sameValue(before[path], delta)) changed[path] = delta;
  }
  const removed = Object.keys(before).filter((path) => !(path in after));
  return { changed, removed };
}
